import { Check, Bug, TriangleAlert, Bot, Minus, Ban } from "lucide-react";
import ScreenshotViewer from "./ScreenshotViewer";
import { cn } from "@/lib/cn";
import { normalizeSteps } from "@/lib/runSteps";

// Map an engine step status to a developer-facing RESULT TYPE. The validator's
// four statuses already carry these exact semantics (see backend
// ValidatorOutput): PASS = evidence confirms; FAIL = evidence contradicts the
// expectation with prerequisites met (a real product defect); INCONCLUSIVE =
// the app may be fine but the test can't prove it; ERROR = the step itself
// failed to run (timeout / selector / exception).
const RESULT = {
  PASS: {
    label: "PASS", emoji: "✅", Icon: Check,
    iconCls: "bg-emerald-500 text-white",
    badgeCls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    devAction: null,
  },
  FAIL: {
    label: "PRODUCT BUG", emoji: "❌", Icon: Bug,
    iconCls: "bg-rose-500 text-white",
    badgeCls: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
    devAction: "The observed result contradicts the expected behavior. Investigate the click handler / navigation / rendering logic for this action.",
  },
  INCONCLUSIVE: {
    label: "VERIFICATION ISSUE", emoji: "⚠", Icon: TriangleAlert,
    iconCls: "bg-amber-500 text-white",
    badgeCls: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    devAction: "The app may be working, but the test couldn't prove it. Add a unique heading, test-id, or visible element so the outcome can be verified.",
  },
  BLOCKED: {
    label: "BLOCKED", emoji: "🚫", Icon: Ban,
    iconCls: "bg-violet-500 text-white",
    badgeCls: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
    devAction: null,
  },
  ERROR: {
    label: "AUTOMATION ISSUE", emoji: "🤖", Icon: Bot,
    iconCls: "bg-slate-500 text-white",
    badgeCls: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
    devAction: "The step failed to execute (timeout, selector, or exception). Check the target element and page timing — this is a test-execution problem, not a confirmed product defect.",
  },
  // The exe agent records an action per step but no per-step verdict, so its
  // steps arrive with `status` unset. Those used to fall through to PASS, which
  // painted five green ticks down a case that had plainly failed — the timeline
  // contradicted the result badge above it. "Not reported" is the honest answer.
  UNKNOWN: {
    label: "NOT REPORTED", emoji: "•", Icon: Minus,
    iconCls: "bg-slate-300 text-white dark:bg-white/20",
    badgeCls: "bg-slate-400/15 text-slate-500 dark:text-slate-400",
    devAction: null,
  },
};

// Engine step statuses are PASS / FAIL / ERROR / INCONCLUSIVE, but some
// surfaces (mock/demo data, run-level rollups) use the "-ED" forms — normalise
// both so a failed step never falls through to the PASS default.
const STATUS_ALIAS = {
  PASS: "PASS", PASSED: "PASS",
  FAIL: "FAIL", FAILED: "FAIL",
  ERROR: "ERROR", ERRORED: "ERROR",
  INCONCLUSIVE: "INCONCLUSIVE",
  BLOCKED: "BLOCKED",
};

function resultFor(status) {
  const key = STATUS_ALIAS[String(status || "").toUpperCase()];
  return RESULT[key] || RESULT.UNKNOWN;
}

// A quoted, capped label for a UI target (button text / field / url).
function tag(target) {
  const t = String(target || "").trim();
  if (!t) return "";
  const short = t.length > 60 ? t.slice(0, 60) + "…" : t;
  return short;
}

// Past-tense, human-language sentence for what the step DID. Prefers the
// planner-supplied `user_action_description`; otherwise synthesises one from
// the action + target (mirrors the backend report's `_humanize_action`).
function describeAction(step) {
  const desc = String(step.description || "").trim();
  if (desc) return desc.replace(/\.$/, "");
  const act = String(step.action || "").trim().toLowerCase();
  const t = tag(step.target);
  const val = step.inputValue;
  switch (act) {
    case "navigate": return t ? `Opened ${t}` : "Opened the application";
    case "click": return t ? `Clicked ${t}` : "Performed a click";
    case "fill":
      if (t && val != null && val !== "") return `Entered “${val}” into ${t}`;
      return t ? `Filled the ${t} field` : "Filled a form field";
    case "wait": return "Waited for the page to settle";
    case "assert":
    case "assert_visible": return t ? `Verified ${t}` : "Verified the expected state";
    case "screenshot": return "Captured the current state";
    default: return t ? `${step.action} ${t}` : (step.action || "Performed the step");
  }
}

// Step-by-step execution timeline: one compact row per step — verdict dot, what
// it did, and the runner's own account of what happened, in full. The previous
// version was a card per step with an Action / Expected / Actual / Evidence /
// Dev action table, which repeated the same sentence up to three times and
// pushed a seven-step run well past a screenful; the failure message — the one
// thing a developer opens this for — ended up below the fold.
//
// Steps are normalised first (see lib/runSteps), so exe, extension and cloud
// runs all render identically here.
// Pass `runId` so a step that captured a screenshot can load it from the
// auth-protected endpoint; `kind` picks which runner's endpoint that is.
export default function StepTimeline({ steps, runId, kind = "cloud", shotIndexes = [], runStatus }) {
  const rows = normalizeSteps(steps, shotIndexes);
  const isBlockedRun = runStatus != null && String(runStatus).toLowerCase() === "blocked";
  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {rows.map((step) => {
          const meta = resultFor(isBlockedRun && step.action === "done" ? "BLOCKED" : step.status);
          const isPass = meta === RESULT.PASS;
          const objective = describeAction(step);
          // The runner's own account of the step. This is the failure message —
          // shown in full, wrapped, never truncated: on a failed case it is the
          // one thing the reader came for.
          const said = step.text && step.text !== objective ? step.text : "";
          return (
            <li
              key={step.index}
              className="flex items-start gap-2.5 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5 dark:border-white/5 dark:bg-white/5"
            >
              <span className={cn("mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full", meta.iconCls)}>
                <meta.Icon size={12} strokeWidth={3} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold capitalize text-slate-800 dark:text-white">
                  {objective}
                  {step.target && (
                    <span className="ml-1.5 font-normal normal-case text-slate-400">{tag(step.target)}</span>
                  )}
                </p>
                {said && (
                  <p className="mt-0.5 whitespace-pre-line break-words text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                    {said}
                  </p>
                )}
                {!isPass && meta.devAction && (
                  <p className="mt-1.5 text-[0.6875rem] italic text-slate-400">{meta.devAction}</p>
                )}
              </div>
              {step.hasShot && runId != null && (
                <ScreenshotViewer
                  runId={runId} stepIndex={step.index} kind={kind}
                  label={`step ${step.index + 1}`}
                />
              )}
            </li>
          );
        })}
      </ul>

      {/* legend — what each result type means */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-xs text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400">
        <p className="mb-1.5 font-semibold text-slate-600 dark:text-slate-300">Result types</p>
        <ul className="space-y-1">
          <li>✅ <b>PASS</b> — expected behavior occurred.</li>
          <li>❌ <b>PRODUCT BUG</b> — application behavior differs from expectation.</li>
          <li>⚠ <b>VERIFICATION ISSUE</b> — application appears correct but the test can&apos;t prove it.</li>
          <li>🤖 <b>AUTOMATION ISSUE</b> — test-execution problem (timeouts, selectors, etc.).</li>
          <li>🚫 <b>BLOCKED</b> — the case was not executed (destructive, manual-only, or a prerequisite was unmet).</li>
        </ul>
      </div>
    </div>
  );
}
